import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../store/authStore'
import { useChatNotifStore } from '../store/chatNotificationStore'

interface ChatEvent {
  type: string
  conversation_id?: string
  message?: any
  [key: string]: any
}

type EventHandler = (event: ChatEvent) => void

interface ChatSocketCtx {
  connected: boolean
  sendTyping: (conversationId: string) => void
  subscribe: (handler: EventHandler) => () => void
  unreadTotal: number
}

const Ctx = createContext<ChatSocketCtx>({
  connected: false,
  sendTyping: () => {},
  subscribe: () => () => {},
  unreadTotal: 0,
})

export function ChatSocketProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient()
  const { accessToken } = useAuthStore()
  const wsRef = useRef<WebSocket | null>(null)
  const handlersRef = useRef<Set<EventHandler>>(new Set())
  const [connected, setConnected] = useState(false)
  const [unreadTotal, setUnreadTotal] = useState(0)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const notifPermRef = useRef(false)

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((p) => { notifPermRef.current = p === 'granted' })
    } else if ('Notification' in window) {
      notifPermRef.current = Notification.permission === 'granted'
    }
  }, [])

  const connect = useCallback(() => {
    if (!accessToken) return
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const envBase = import.meta.env.VITE_API_BASE_URL
    const host = envBase ? envBase.replace(/^https?:\/\//, '') : window.location.host
    const url = `${protocol}://${host}/api/v1/chat/ws?token=${accessToken}`

    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => setConnected(true)

    // Diagnostic — the close code/reason aren't visible anywhere in the
    // browser's Network panel (a healthy idle socket and a rejected one
    // both show "no messages" there, since neither exchanges app-level
    // frames), so log them directly. Cheap, and safe to leave in
    // permanently — one line per connection lifecycle, not per message.
    const openedAt = Date.now()

    ws.onmessage = (ev) => {
      try {
        const event: ChatEvent = JSON.parse(ev.data)

        // Notify all subscribers
        for (const handler of handlersRef.current) handler(event)

        // Invalidate relevant queries
        if (event.type === 'new_message') {
          qc.invalidateQueries({ queryKey: ['chat-messages', event.conversation_id] })
          qc.invalidateQueries({ queryKey: ['chat-conversations'] })

          const senderName = event.message?.sender_name ?? 'Someone'
          const text = event.message?.text ?? ''
          const senderId = event.message?.sender_id

          // In-app chat notification (not on chat page, not own message)
          if (!window.location.pathname.startsWith('/chats') && senderId) {
            const initials = senderName.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2)
            let hash = 0
            for (const c of senderId) hash = ((hash << 5) - hash + c.charCodeAt(0)) | 0
            const colors = ['#0D9488','#D97706','#2563EB','#DC2626','#7C3AED','#EA580C','#059669','#DB2777']
            useChatNotifStore.getState().show({
              senderName, senderInitials: initials,
              senderColor: colors[Math.abs(hash) % colors.length]!,
              text, conversationId: event.conversation_id!,
            })
          }

          // Browser notification when tab is hidden
          if (notifPermRef.current && document.hidden && senderId) {
            try {
              const notif = new Notification(senderName, {
                body: text.slice(0, 100), icon: '/favicon.ico',
                tag: `chat-${event.conversation_id}`,
              })
              notif.onclick = () => { window.focus(); window.location.href = `/chats/${event.conversation_id}`; notif.close() }
            } catch {}
          }
        }

        if (event.type === 'reaction_added' || event.type === 'reaction_removed') {
          qc.invalidateQueries({ queryKey: ['chat-messages', event.conversation_id] })
        }

        if (event.type === 'message_pinned' || event.type === 'message_unpinned') {
          qc.invalidateQueries({ queryKey: ['chat-messages', event.conversation_id] })
        }

        if (event.type === 'message_deleted') {
          qc.invalidateQueries({ queryKey: ['chat-messages', event.conversation_id] })
        }

        if (event.type === 'conversation_created') {
          qc.invalidateQueries({ queryKey: ['chat-conversations'] })
        }

        // Help & Support feedback threads — reused by both the
        // org-user Help page and the super-admin triage queue, so
        // both directions of the conversation update live.
        if (event.type === 'feedback_message') {
          qc.invalidateQueries({ queryKey: ['feedback', 'messages', event.feedback_id] })
          qc.invalidateQueries({ queryKey: ['feedback', 'mine'] })
          qc.invalidateQueries({ queryKey: ['super-admin', 'feedback', 'messages', event.feedback_id] })
          qc.invalidateQueries({ queryKey: ['super-admin', 'feedback'] })
        }
        if (event.type === 'feedback_created') {
          qc.invalidateQueries({ queryKey: ['super-admin', 'feedback'] })
        }
        if (event.type === 'feedback_message_deleted') {
          qc.invalidateQueries({ queryKey: ['feedback', 'messages', event.feedback_id] })
          qc.invalidateQueries({ queryKey: ['super-admin', 'feedback', 'messages', event.feedback_id] })
        }
        if (event.type === 'feedback_deleted') {
          qc.invalidateQueries({ queryKey: ['feedback', 'mine'] })
          qc.invalidateQueries({ queryKey: ['super-admin', 'feedback'] })
        }

        // Work order events
        if (event.type === 'work_order_updated') {
          qc.invalidateQueries({ queryKey: ['work-orders'] })

          // In-app notification
          if (!window.location.pathname.startsWith('/work-orders')) {
            const woCode = (event.wo_code as string) ?? 'Work Order'
            const msg = (event.message as string) ?? 'Status updated'
            useChatNotifStore.getState().show({
              senderName: woCode, senderInitials: 'WO',
              senderColor: '#0D9488', text: msg,
              conversationId: '',
            })
          }

          // Browser notification
          if (notifPermRef.current && document.hidden) {
            try {
              const notif = new Notification((event.wo_code as string) ?? 'Work Order', {
                body: (event.message as string) ?? 'Status updated',
                icon: '/favicon.ico', tag: `wo-${event.work_order_id}`,
              })
              notif.onclick = () => { window.focus(); window.location.href = '/work-orders'; notif.close() }
            } catch {}
          }
        }
      } catch {}
    }

    ws.onclose = (ev) => {
      // eslint-disable-next-line no-console
      console.log(
        `[chat-ws] closed after ${Date.now() - openedAt}ms — code=${ev.code} reason="${ev.reason}" clean=${ev.wasClean}`,
      )
      setConnected(false)
      reconnectTimer.current = setTimeout(connect, 3000)
    }

    ws.onerror = (ev) => {
      // eslint-disable-next-line no-console
      console.log('[chat-ws] error event', ev)
      ws.close()
    }
  }, [accessToken, qc])

  useEffect(() => {
    connect()
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current)
      wsRef.current?.close()
    }
  }, [connect])

  // Poll unread total for badge
  useEffect(() => {
    if (!accessToken) return
    const poll = async () => {
      try {
        const convs = qc.getQueryData<any[]>(['chat-conversations'])
        if (Array.isArray(convs)) {
          setUnreadTotal(convs.reduce((sum, c) => sum + (c.unread_count ?? 0), 0))
        }
      } catch {}
    }
    poll()
    const interval = setInterval(poll, 5000)
    return () => clearInterval(interval)
  }, [accessToken, qc])

  const sendTyping = useCallback((conversationId: string) => {
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: 'typing', conversation_id: conversationId }))
    }
  }, [])

  const subscribe = useCallback((handler: EventHandler) => {
    handlersRef.current.add(handler)
    return () => { handlersRef.current.delete(handler) }
  }, [])

  return (
    <Ctx.Provider value={{ connected, sendTyping, subscribe, unreadTotal }}>
      {children}
    </Ctx.Provider>
  )
}

export function useChatSocket() {
  return useContext(Ctx)
}
